import React, { useCallback, useEffect, useState, useRef } from "react";
import { Tooltip, Alert } from "antd";
import { useLocation, useNavigate } from "react-router-dom";
import {
  EditOutlined,
  DeleteOutlined,
  PlusOutlined,
  BarChartOutlined,
  UserAddOutlined,
} from "@ant-design/icons";
import { Button, PageHeader } from "../../components/Common";
import PaginatedTable from "../../components/PaginatedTable";
import CredentialsModal from "../Users/CredentialsModal";
import { useConfirm } from "../../hooks/useConfirm";
import { useMutations } from "../../hooks/useApiMutation";

const ClientsList = () => {
  // One-time portal credentials can arrive from ClientFormPage via navigation
  // state (they are returned exactly once by the create endpoint).
  const location = useLocation();
  const [portalCredentials, setPortalCredentials] = useState(
    location.state?.portalCredentials ?? null
  );
  const [portalWarning, setPortalWarning] = useState(
    location.state?.portalWarning ?? null
  );
  const { mutate } = useMutations();
  const { confirmAction } = useConfirm();
  const navigate = useNavigate();
  const tableRef = useRef(null);

  useEffect(() => {
    // Clear the state so a refresh doesn't re-show stale credentials.
    if (location.state?.portalCredentials || location.state?.portalWarning) {
      window.history.replaceState({}, "");
    }
  }, [location.state]);

  const fetchData = useCallback(
    async (page, pageSize, search) => {
      let result = { data: [], total: 0 };

      await mutate("fetchClients", {
        method: "get",
        endpoint: "/clients",
        params: { page, pageSize, search },
        onSuccess: (res) => {
          result = {
            data: res?.items || [],
            total: res?.totalCount || 0,
          };
        },
        onError: () => {
          result = { data: [], total: 0 };
        },
      });
      return result;
    },
    [mutate]
  );

  const columns = [
    { title: "Name", dataIndex: "name", key: "name" },
    {
      title: "Organization",
      dataIndex: "organizationName",
      key: "organizationName",
    },
    { title: "Email", dataIndex: "email", key: "email" },
    { title: "Phone", dataIndex: "phone", key: "phone" },
    { title: "Fiscal Nr.", dataIndex: "fiscalNumber", key: "fiscalNumber" },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <>
          <Tooltip title="Usage">
            <Button
              variant="ghost"
              icon={<BarChartOutlined />}
              onClick={() => navigate(`/clients/${record.companyId}/usage`)}
            />
          </Tooltip>
          <Tooltip title="Issue a portal login">
            <Button
              variant="ghost"
              icon={<UserAddOutlined />}
              onClick={() =>
                confirmAction({
                  key: "createPortalUser",
                  method: "post",
                  endpoint: `/clients/${record.companyId}/portal-users`,
                  title: "Issue portal login",
                  content: `Create a client-portal account for "${record.name}"? A temporary password is generated and emailed to them.`,
                  okText: "Create",
                  onSuccess: (data) => setPortalCredentials(data),
                })
              }
            />
          </Tooltip>
          <Tooltip title="Edit">
            <Button
              variant="ghost"
              icon={<EditOutlined />}
              onClick={() =>
                navigate(`/clients/${record.companyId}/edit`, {
                  state: { record },
                })
              }
            />
          </Tooltip>
          <Tooltip title="Delete">
            <Button
              variant="ghost"
              danger
              icon={<DeleteOutlined />}
              onClick={() =>
                confirmAction({
                  key: "deleteClient",
                  method: "delete",
                  endpoint: `/clients/${record.companyId}`,
                  title: "Delete Confirmation",
                  content: "Are you sure you want to delete this item?",
                  okText: "Delete",
                  okType: "danger",
                  onSuccess: () => tableRef.current?.reloadPage(),
                })
              }
            />
          </Tooltip>
        </>
      ),
    },
  ];

  return (
    <div>
      <PageHeader
        title="Clients"
        subtitle="Companies with access to the platform and their portal accounts."
        extra={
          <Button
            variant="primary"
            icon={<PlusOutlined />}
            onClick={() => navigate("/clients/new")}
          >
            Create Client
          </Button>
        }
      />

      {portalWarning && (
        <Alert
          type="warning"
          showIcon
          closable
          onClose={() => setPortalWarning(null)}
          style={{ marginBottom: 16 }}
          message="Client created, but no portal login was issued"
          description={`${portalWarning} You can issue one from the client's row.`}
        />
      )}

      <PaginatedTable
        ref={tableRef}
        columns={columns}
        fetchData={fetchData}
        rowKey="companyId"
      />

      <CredentialsModal
        credentials={portalCredentials}
        onClose={() => setPortalCredentials(null)}
        title="Client portal login"
      />
    </div>
  );
};

export default ClientsList;
